/**
 * useConnectionsList — pagination-aware connection list hook.
 *
 * `useConnections` (in `useConnectionMutations.ts`) is the primary consumer for
 * live connection data across the app. It returns the normalized
 * `AvailableConnection[]` array without pagination metadata, which is what
 * most callers need (pickers, tabs, status validators).
 *
 * `useConnectionsMeta` here exposes the *walk* metadata for the connection
 * picker header — specifically `truncated` (list is incomplete because the
 * server returned more pages than `CONNECTIONS_MAX_PAGES` OR a hostile
 * cross-origin `nextLink` was rejected) and `fetchedPages` (how many pages
 * we actually walked). The picker uses this to render an honest
 * "showing first N pages" warning instead of silently rendering a partial
 * list — the exact failure #2522's original reviewer flagged.
 *
 * Both hooks share a single React Query entry via `connectionsListQueryOptions`
 * (defined next to `useConnections`). React Query de-dupes the paginated walk
 * when both are mounted concurrently — each hook adds its own `select`
 * projection over the shared `ListConnectionsResult` cache.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { CONNECTIONS_MAX_PAGES } from '@/services/connectionApi';
import { connectionsListQueryOptions, useAppContextBuilder } from './useConnectionMutations';

export interface ConnectionsMeta {
  truncated: boolean;
  fetchedPages: number;
  maxPages: number;
}

/**
 * Return only the pagination metadata for the current app's connection list.
 *
 * Shares the `connectionKeys.list(appId, connectorId)` cache entry with
 * `useConnections` — mounting both hooks in the same component fires a
 * single paginated walk; this hook simply projects the metadata half of
 * the cached `ListConnectionsResult`.
 */
export function useConnectionsMeta(
  autoAppId?: string,
  connectorId?: string,
  enabled = true,
): UseQueryResult<ConnectionsMeta, Error> {
  const effectiveAppId = autoAppId || undefined;
  const { buildContext, appId } = useAppContextBuilder(effectiveAppId);

  return useQuery({
    ...connectionsListQueryOptions(appId, connectorId, buildContext),
    select: (result) => ({
      truncated: result.truncated,
      fetchedPages: result.fetchedPages,
      maxPages: CONNECTIONS_MAX_PAGES,
    }),
    enabled: enabled && !!appId,
  });
}
